import { Box, Button, CircularProgress, Divider } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import OrderStepper from "./OrderStepper";
import { Payments } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { fetchOrderById, fetchOrderItemById } from "../../../State/customer/orderSlice";

const OrderDetails = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { orderId, orderItemId } = useParams();
    const { order } = useAppSelector((store) => store);

    useEffect(() => {
        if (orderId && orderItemId) {
            const jwt = localStorage.getItem("jwt") || "";
            dispatch(fetchOrderById({ orderId: Number(orderId), jwt }));
            dispatch(fetchOrderItemById({ orderItemId: Number(orderItemId), jwt }));
        }
    }, [dispatch, orderId, orderItemId]);

    const currentOrder = order.currentOrder;
    const orderItem = order.orderItem;

    // Show loading spinner while fetching initial data
    if (order.loading || !currentOrder || !orderItem) {
        return (
            <Box className="min-h-[60vh] flex justify-center items-center">
                <CircularProgress />
            </Box>
        );
    }

    const shippingAddress = currentOrder.shippingAddress;
    const product = orderItem.product;
    const sellerName = product?.seller?.businessDetails?.businessName || "Vendor";
    const productImage = product?.images?.[0] || "";
    const isCancelled = currentOrder.orderStatus === "CANCELLED";

    return (
        <Box className="space-y-5">
            {/* Header / Product Info */}
            <section className="flex flex-col gap-5 justify-center items-center">
                {productImage && (
                    <img className="w-[100px] object-cover" src={productImage} alt={product?.title || "Product"} />
                )}

                <div className="text-sm space-y-1 text-center">
                    <h1 className="font-bold">{sellerName}</h1>
                    <p>{product?.title}</p>
                </div>
            </section>

            {/* Stepper Status */}
            <section className="border p-5">
                <OrderStepper orderStatus={currentOrder.orderStatus || "PENDING"} />
            </section>

            {/* Delivery Address */}
            <div className="border p-5">
                <h1 className="font-bold pb-3">Delivery Address</h1>
                <div className="text-sm space-y-2">
                    <div className="flex gap-5 font-medium">
                        <p>{shippingAddress?.name || "N/A"}</p>
                        <Divider flexItem orientation="vertical" />
                        <p>{shippingAddress?.mobile || "N/A"}</p>
                    </div>

                    <p>
                        {shippingAddress?.address ? `${shippingAddress.address}, ` : ""}
                        {shippingAddress?.locality ? `${shippingAddress.locality}, ` : ""}
                        {shippingAddress?.city ? `${shippingAddress.city}, ` : ""}
                        {shippingAddress?.state ? `${shippingAddress.state} - ` : ""}
                        {shippingAddress?.pinCode || ""}
                    </p>
                </div>
            </div>

            {/* Price Details & Payment Status */}
            <div className="border space-y-4">
                <div className="flex justify-between text-sm pt-5 px-5">
                    <div className="space-y-1">
                        <p className="font-bold">Total Item Price</p>
                        <p>
                            You saved{" "}
                            <span className="text-green-500 font-medium text-xs">
                                Ksh {(orderItem.mrpPrice - orderItem.sellingPrice).toLocaleString()}.00
                            </span>{" "}
                            on this item
                        </p>
                    </div>

                    <p className="font-medium">Ksh {orderItem.sellingPrice?.toLocaleString()}.00</p>
                </div>

                <div className="px-5">
                    <div className="bg-teal-50 px-5 py-2 text-xs font-medium flex items-center gap-3">
                        <Payments />
                        <p>Payment Status: {currentOrder.paymentDetails?.paymentStatus || "PENDING"}</p>
                    </div>
                </div>

                <Divider />

                <div className="px-5 pb-5">
                    <p className="text-xs">
                        <strong>Sold by : </strong>{sellerName}
                    </p>
                </div>

                {/* Cancel Action */}
                <div className="p-10">
                    <Button
                        disabled={isCancelled}
                        color="error"
                        sx={{ py: "0.7rem" }}
                        variant="outlined"
                        fullWidth
                    >
                        {isCancelled ? "Order Cancelled" : "Cancel Order"}
                    </Button>
                </div>
            </div>
        </Box>
    );
};

export default OrderDetails;